import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { authApi } from '../../api/auth';
import { administersPublicTenant } from '../../config/adminRoles';
import { FeatureGates } from './FeatureGates';
import { Experiments } from './Experiments';
import { RateLimits } from './RateLimits';
import { Defaults } from './Defaults';
import { ApiMetrics } from './ApiMetrics';
import { AsyncJobSettings } from './AsyncJobSettings';
import { WhatsAppSettings } from './WhatsAppSettings';
import { SmsSettings } from './SmsSettings';
import { Email } from './Email';
import { Notifications } from './Notifications';
import { ConfigSync } from './ConfigSync';
import { AdCampaigns } from './AdCampaigns';
import './Settings.css';

type SettingsTab = 'defaults' | 'feature-gates' | 'experiments' | 'rate-limits' | 'async-jobs' | 'api-metrics' | 'whatsapp' | 'sms' | 'email' | 'notifications' | 'ad-campaigns' | 'config-sync';

export function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab: SettingsTab = (searchParams.get('tab') as SettingsTab) || 'defaults';

  const setActiveTab = useCallback((tab: SettingsTab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  /* The WhatsApp WABA, the MSG91 account and the Meta ad account are the
     PLATFORM's, not a tenant's, so their tabs belong to whoever administers
     the public tenant.
     Both the button and the panel are gated: every panel below is mounted and
     merely hidden with CSS, so leaving the panel in place would keep firing its
     request — and a 403 on page load, for a tab nobody opened, is how this was
     noticed. */
  const platformChannels = administersPublicTenant(authApi.getUser());

  /* A shared `?tab=whatsapp` link reaching someone without the authority would
     otherwise select a tab that renders nothing — a blank page rather than a
     refusal. Land them on Defaults instead. */
  const PLATFORM_TABS: SettingsTab[] = ['whatsapp', 'sms', 'ad-campaigns'];
  const activeTab: SettingsTab =
    !platformChannels && PLATFORM_TABS.includes(requestedTab) ? 'defaults' : requestedTab;

  const { data: settings, isLoading: settingsLoading, error: settingsError } = useQuery({
    queryKey: ['settings'],
    queryFn: adminApi.getSettings,
  });

  if (settingsLoading) return <div className="loading">Loading settings...</div>;
  if (settingsError) return <div className="error">Error: {(settingsError as Error).message}</div>;

  return (
    <div className="settings-page" data-active-tab={activeTab}>
      <h1>Settings</h1>

      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'defaults' ? 'active' : ''}`}
          onClick={() => setActiveTab('defaults')}
        >
          Defaults
        </button>
        <button
          className={`settings-tab ${activeTab === 'feature-gates' ? 'active' : ''}`}
          onClick={() => setActiveTab('feature-gates')}
        >
          Feature Gates
        </button>
        <button
          className={`settings-tab ${activeTab === 'experiments' ? 'active' : ''}`}
          onClick={() => setActiveTab('experiments')}
        >
          Experiments
        </button>
        <button
          className={`settings-tab ${activeTab === 'rate-limits' ? 'active' : ''}`}
          onClick={() => setActiveTab('rate-limits')}
        >
          Rate Limits
        </button>
        <button
          className={`settings-tab ${activeTab === 'async-jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('async-jobs')}
        >
          Async Jobs
        </button>
        <button
          className={`settings-tab ${activeTab === 'api-metrics' ? 'active' : ''}`}
          onClick={() => setActiveTab('api-metrics')}
        >
          API Metrics
        </button>
        {platformChannels && (
          <button
            className={`settings-tab ${activeTab === 'whatsapp' ? 'active' : ''}`}
            onClick={() => setActiveTab('whatsapp')}
          >
            WhatsApp
          </button>
        )}
        {platformChannels && (
          <button
            className={`settings-tab ${activeTab === 'sms' ? 'active' : ''}`}
            onClick={() => setActiveTab('sms')}
          >
            SMS
          </button>
        )}
        <button
          className={`settings-tab ${activeTab === 'email' ? 'active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          Email
        </button>
        <button
          className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
          onClick={() => setActiveTab('notifications')}
        >
          Notifications
        </button>
        {platformChannels && (
          <button
            className={`settings-tab ${activeTab === 'ad-campaigns' ? 'active' : ''}`}
            onClick={() => setActiveTab('ad-campaigns')}
          >
            Ad Campaigns
          </button>
        )}
        <button
          className={`settings-tab ${activeTab === 'config-sync' ? 'active' : ''}`}
          onClick={() => setActiveTab('config-sync')}
        >
          Config Sync
        </button>
      </div>

      <div style={{ display: activeTab === 'defaults' ? undefined : 'none' }}><Defaults /></div>
      <div style={{ display: activeTab === 'feature-gates' ? undefined : 'none' }}><FeatureGates /></div>
      <div style={{ display: activeTab === 'experiments' ? undefined : 'none' }}><Experiments /></div>
      <div style={{ display: activeTab === 'rate-limits' ? undefined : 'none' }}><RateLimits /></div>
      <div style={{ display: activeTab === 'api-metrics' ? undefined : 'none' }}><ApiMetrics /></div>
      <div style={{ display: activeTab === 'async-jobs' ? undefined : 'none' }}><AsyncJobSettings /></div>
      {platformChannels && <div style={{ display: activeTab === 'whatsapp' ? undefined : 'none' }}><WhatsAppSettings /></div>}
      {platformChannels && <div style={{ display: activeTab === 'sms' ? undefined : 'none' }}><SmsSettings /></div>}
      <div style={{ display: activeTab === 'email' ? undefined : 'none' }}><Email /></div>
      <div style={{ display: activeTab === 'notifications' ? undefined : 'none' }}><Notifications /></div>
      {platformChannels && <div style={{ display: activeTab === 'ad-campaigns' ? undefined : 'none' }}><AdCampaigns /></div>}
      <div style={{ display: activeTab === 'config-sync' ? undefined : 'none' }}><ConfigSync /></div>

      {/* Defaults only — `settings.updatedAt` describes the app-settings doc that
          tab edits, so it is misleading under Feature Gates, Async Jobs, etc. */}
      {activeTab === 'defaults' && (
      <div className="settings-footer">
        {settings?.updatedAt && (
          <p className="last-updated">
            Last updated: {format(new Date(settings.updatedAt), 'PPpp')}
          </p>
        )}

        <div className="build-info">
          <div>API: {import.meta.env.VITE_API_BASE || '(default)'}</div>
          <div>Build: {format(new Date(__BUILD_TIMESTAMP__), 'MMM d, yyyy HH:mm:ss')}</div>
        </div>
      </div>
      )}
    </div>
  );
}
